import { EventType } from "@/analytics/analytics-events";
import {
  SelectCadElementEventProperties,
  SelectCadParentElementEventProperties,
} from "@/analytics/cad-bim-events-properties";
import {
  CadModelSetVisibilityEvent,
  useSceneEvents,
} from "@/components/common/scene-events-context";
import { selectActiveCadId } from "@/store/cad/cad-slice";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { useTypedEvent } from "@faro-lotv/app-component-toolbox";
import { NO_TRANSLATE_CLASS } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import { NodeApi, Tree, TreeApi } from "react-arborist";
import { useDragDropManager } from "react-dnd";
import { TREE_NODE_HEIGHT } from "../tree-node";
import { CadModelTreeNodeData } from "./cad-model-tree-data";
import { CadModelTreeNode } from "./cad-model-tree-node";
import { CadModelTreeRow } from "./cad-model-tree-row";
import {
  selectModelTreeNode,
  setNodeVisibility,
  unselectAllNodes,
} from "./cad-model-visibility";

/** Parameters for the CadModelTree component */
type CadModelTreeProps = {
  /** Cad model tree to display */
  modelTreeData: CadModelTreeNodeData[];

  /** if true, user owns valid CAD license */
  hasValidCadLicense: boolean;

  /** Height to apply to the Tree component */
  height?: number;
};

/**
 * Display the CAD model tree UI component.
 *
 * @param props Tree data, license and layout
 * @returns The CAD model tree.
 */
export function CadModelTree(props: CadModelTreeProps): JSX.Element {
  const activeCad = useAppSelector(selectActiveCadId);
  return <CadModelTreeContent key={activeCad} {...props} />;
}

function CadModelTreeContent({
  modelTreeData,
  hasValidCadLicense,
  height,
}: CadModelTreeProps): JSX.Element {
  const dragDropManager = useDragDropManager();
  const treeRef = useRef<TreeApi<CadModelTreeNodeData>>(undefined);
  const [openState, setOpenState] = useState<Record<string, boolean>>(() => ({
    [modelTreeData[0]?.id]: true,
  }));
  const [pendingSelection, setPendingSelection] = useState<{
    node: CadModelTreeNodeData;
    history: CadModelTreeNodeData[];
  }>();
  const selectHistory = useRef<CadModelTreeNodeData[]>([]);
  const activeTool = useAppSelector(selectActiveTool);

  const cadModelData = modelTreeData.length
    ? modelTreeData[0].cadModelData
    : null;

  const canExpand = !cadModelData || hasValidCadLicense;

  const childrenAccessor = useCallback(
    (node: CadModelTreeNodeData): CadModelTreeNodeData[] | null => {
      if (!canExpand) return null;
      // An empty array keeps the expand arrow without allocating hidden NodeApis.
      return node.children && (openState[node.id] ? node.children : []);
    },
    [canExpand, openState],
  );

  const handleToggle = useCallback(() => {
    if (treeRef.current) setOpenState(treeRef.current.openState);
  }, []);

  const updateSelectedTreeNode = useCallback(
    (
      node: CadModelTreeNodeData,
      history: CadModelTreeNodeData[] = [],
    ): void => {
      if (!treeRef.current || (!canExpand && node.parent)) return;
      const treeNode = treeRef.current.get(node.id);
      if (treeNode) {
        treeNode.select();
        selectHistory.current = history;
        setPendingSelection(undefined);
      } else {
        // Arborist cannot discover ancestors that have not been materialized.
        let { parent } = node;
        while (parent) {
          treeRef.current.open(parent.id);
          ({ parent } = parent);
        }
        setPendingSelection({ node, history });
      }
    },
    [canExpand],
  );

  useEffect(() => {
    if (!pendingSelection || !treeRef.current) return;
    const node = treeRef.current.get(pendingSelection.node.id);
    if (node) {
      node.select();
      selectHistory.current = pendingSelection.history;
      setPendingSelection(undefined);
    }
  }, [pendingSelection, openState]);

  const cadSceneEvents = useSceneEvents();

  const unselectAllTreeNodes = useCallback((): void => {
    if (!treeRef.current) return;
    if (treeRef.current.selectedNodes.length > 0) {
      treeRef.current.deselectAll();
    }
    if (
      cadModelData?.selectedObjects &&
      cadModelData.selectedObjects.length > 0
    ) {
      unselectAllNodes(cadModelData);
      cadSceneEvents.invalidateCadScene.emit();
    }
    selectHistory.current = [];
    setPendingSelection(undefined);
  }, [cadModelData, cadSceneEvents.invalidateCadScene]);

  const onSetCadModelVisibility = useCallback(
    (e: CadModelSetVisibilityEvent) => {
      if (!treeRef.current || !cadModelData) return;

      const data = cadModelData.treeNodeData.get(e.objectId);
      if (!data) {
        console.warn(`CAD object ${e.objectId} not found in the model tree.`);
        return;
      }

      setNodeVisibility(data, e.visibility, e.isolate);
      cadSceneEvents.invalidateCadScene.emit();
      updateSelectedTreeNode(data);
    },
    [cadModelData, cadSceneEvents.invalidateCadScene, updateSelectedTreeNode],
  );

  useTypedEvent<CadModelSetVisibilityEvent>(
    cadSceneEvents.setCadModelVisibility,
    onSetCadModelVisibility,
  );

  const onSelectParentCadObjectInTree = useCallback(
    (objectId: number) => {
      if (!cadModelData) return;

      const data = cadModelData.treeNodeData.get(objectId);
      if (!data) {
        console.warn(`CAD object ${objectId} not found in the model tree.`);
        return;
      }

      // add child node to history, so "Shift-tab" will go back down to the clicked element
      updateSelectedTreeNode(data.parent ?? data, [data]);
    },
    [cadModelData, updateSelectedTreeNode],
  );

  useTypedEvent<number>(
    cadSceneEvents.selectParentCadObjectInTree,
    onSelectParentCadObjectInTree,
  );

  const onLocateCadObjectInTree = useCallback(
    (objectId: number | undefined) => {
      if (objectId === undefined) {
        unselectAllTreeNodes();
        return;
      }
      const data = cadModelData?.treeNodeData.get(objectId);
      if (!data) {
        console.warn(`CAD object ${objectId} not found in the model tree.`);
        return;
      }

      updateSelectedTreeNode(data);
      // need to update the selected state of the node, the `onSelect` event seems not received the node
      selectModelTreeNode(data);
      Analytics.track<SelectCadElementEventProperties>(
        EventType.selectCadElement,
        {
          via: "rightClick",
        },
      );
      cadSceneEvents.invalidateCadScene.emit();
      // clear selected node history
      selectHistory.current = [];
    },
    [
      cadModelData?.treeNodeData,
      cadSceneEvents.invalidateCadScene,
      unselectAllTreeNodes,
      updateSelectedTreeNode,
    ],
  );

  useTypedEvent<number | undefined>(
    cadSceneEvents.locateCadObjectInTree,
    onLocateCadObjectInTree,
  );

  // reset selection of cad nodes at cad switch
  useEffect(() => {
    unselectAllTreeNodes();
    return () => {
      if (cadModelData?.selectedObjects.length) {
        unselectAllNodes(cadModelData);
        cadSceneEvents.invalidateCadScene.emit();
      }
    };
  }, [cadModelData, cadSceneEvents.invalidateCadScene, unselectAllTreeNodes]);

  // This overrides the deselectAll method of TreeApi to handle properly the deselection
  // when user click in the empty space under the CAD tree.
  useEffect(() => {
    const tree = treeRef.current;
    if (!tree) return;
    const originalDeselectAll = tree.deselectAll.bind(tree);
    tree.deselectAll = () => {
      if (cadModelData?.selectedObjects.length) {
        unselectAllNodes(cadModelData);
        cadSceneEvents.invalidateCadScene.emit();
      }
      originalDeselectAll();
    };
    return () => {
      tree.deselectAll = originalDeselectAll;
    };
  }, [
    cadModelData,
    cadModelData?.selectedObjects.length,
    cadSceneEvents.invalidateCadScene,
    treeRef,
    unselectAllTreeNodes,
  ]);

  // Setup keyboard shortcuts
  // - "Tab" should select one parent level above until reaching root
  // - "Shift+Tab" should go down the tree from where we came up
  // - "Esc" should deselect the current selected node if there is no active tool
  useEffect(() => {
    function keyDown(ev: KeyboardEvent): void {
      if (!treeRef.current) return;

      switch (ev.key) {
        case "Tab": {
          ev.stopPropagation();
          const selected = treeRef.current.selectedNodes;
          if (selected.length !== 1) return;

          if (!ev.shiftKey) {
            if (selected[0].id !== modelTreeData[0]?.id) {
              // backup current selected node history, change selected will clear the history
              const history = selectHistory.current.slice();
              selected[0].parent?.select();
              history.push(selected[0].data);
              selectHistory.current = history;

              Analytics.track<SelectCadParentElementEventProperties>(
                EventType.selectCadParentElement,
                {
                  via: "tabKey",
                },
              );
            }
          } else if (selectHistory.current.length) {
            // backup current selected node history, change selected will clear the history
            const history = selectHistory.current.slice();
            const lastSelected = history.pop();
            if (lastSelected) updateSelectedTreeNode(lastSelected, history);

            Analytics.track(EventType.selectCadChildrenElement);
          }
          break;
        }
        case "Escape": {
          if (activeTool === null) {
            ev.stopPropagation();
            unselectAllTreeNodes();
            treeRef.current.hideCursor();
          }
          break;
        }
      }
    }

    window.addEventListener("keydown", keyDown);

    return () => {
      window.removeEventListener("keydown", keyDown);
    };
  }, [
    modelTreeData,
    treeRef,
    cadModelData,
    cadSceneEvents,
    selectHistory,
    unselectAllTreeNodes,
    activeTool,
    updateSelectedTreeNode,
  ]);

  const handleNodeSelect = useCallback(
    (nodes: Array<NodeApi<CadModelTreeNodeData>>): void => {
      if (nodes.length === 0) return;
      selectModelTreeNode(nodes[0].data);
      cadSceneEvents.invalidateCadScene.emit();
      // clear selected node history
      selectHistory.current = [];
    },
    [cadSceneEvents.invalidateCadScene],
  );

  return (
    <Tree
      dndManager={dragDropManager}
      data={modelTreeData}
      childrenAccessor={childrenAccessor}
      className={NO_TRANSLATE_CLASS}
      renderRow={CadModelTreeRow}
      openByDefault={false}
      width="100%"
      initialOpenState={{ [modelTreeData[0]?.id]: true }}
      onToggle={handleToggle}
      indent={12}
      height={height}
      rowHeight={TREE_NODE_HEIGHT}
      ref={treeRef}
      onSelect={handleNodeSelect}
      onClick={() =>
        Analytics.track<SelectCadElementEventProperties>(
          EventType.selectCadElement,
          {
            via: "treeElement",
          },
        )
      }
      disableMultiSelection
    >
      {CadModelTreeNode}
    </Tree>
  );
}
